/*
 * <delivery-date-picker>
 *
 * Lightweight custom delivery date calendar for the CHAG cart drawer. No
 * library, no app. Renders a month grid, greys out unavailable days, and writes
 * the chosen date to the cart attribute "Delivery Date" (YYYY-MM-DD) via
 * /cart/update.js so it flows to the order and ShipStation.
 *
 * Rules are passed in via data attributes from the `delivery_rules` metaobject:
 *   data-cutoff-time   "14:00"  next-day cutoff in Europe/London (HH:MM)
 *   data-window-days   "90"     bookable window, today -> today + window_days
 *   data-working-days  "1,2,3,4,5,6"  ISO-ish day numbers we deliver on
 *                                       (1=Mon ... 6=Sat ; 0/7 = Sunday)
 *   data-blackout-dates "YYYY-MM-DD,YYYY-MM-DD,..."  non-collection days
 *   data-current-date   the date already on the cart (re-render persistence)
 *
 * CRITICAL: "now" is computed in Europe/London regardless of the visitor's
 * timezone, using Intl.DateTimeFormat parts (handles BST automatically). All
 * day maths is done on calendar dates (Y/M/D), never on UTC timestamps, so a
 * visitor in another timezone sees the same grid as someone in the UK.
 */
if (!customElements.get('delivery-date-picker')) {
  customElements.define(
    'delivery-date-picker',
    class DeliveryDatePicker extends HTMLElement {
      connectedCallback() {
        // Guard against double-init if connectedCallback fires twice.
        if (this._initialised) return;
        this._initialised = true;

        // Everything from here is wrapped so a single unexpected failure (e.g.
        // malformed data attribute, an Intl edge case) NEVER leaves the customer
        // with an invisible/unusable control. On any throw we fall back to the
        // server-rendered native <input type="date">, which is fully functional.
        try {
          this.boundFallbackInput = this.querySelector('[data-dd-fallback-input]');
          this.calendarEl = this.querySelector('[data-dd-calendar]');
          this.summaryEl = this.querySelector('[data-dd-summary]');
          this.selectedEl = this.querySelector('[data-dd-selected]');
          this.changeBtn = this.querySelector('[data-dd-change]');
          this.input = this.querySelector('[data-dd-input]');
          this.errorEl = this.querySelector('[data-dd-error]');

          this.parseRules();

          // The grid month currently shown ({y, m} where m is 0-11).
          this.viewMonth = null;
          // The currently selected date as a YYYY-MM-DD string, or null.
          this.selected = null;

          if (this.changeBtn) {
            this.changeBtn.addEventListener('click', () => this.openCalendar());
          }

          // Keyboard navigation is delegated on the calendar container.
          if (this.calendarEl) {
            this.calendarEl.addEventListener('keydown', (e) => this.onGridKeydown(e));
          }

          this.boot();

          // Success: take over from the native fallback.
          this.activateRichPicker();
        } catch (err) {
          // Keep the native fallback usable; log for debugging, do not rethrow.
          this.revealFallback();
          if (window && window.console) console.error('[delivery-date-picker] init failed; native fallback shown', err);
        }
      }

      /*
       * Hand off from the no-JS native input to the rich calendar:
       *  - mark the host ready so CSS hides the fallback + reveals the picker
       *  - give the rich hidden input its cart-attribute name so IT submits
       *  - strip the fallback input's name so it does NOT double-submit
       */
      activateRichPicker() {
        if (this.input) this.input.setAttribute('name', 'attributes[Delivery Date]');
        if (this.boundFallbackInput) this.boundFallbackInput.removeAttribute('name');
        this.classList.add('dd--ready');
      }

      /*
       * Failure path: ensure the native fallback is the active, submitting field
       * (it keeps its name) and is visible (no dd--ready class on the host).
       */
      revealFallback() {
        this.classList.remove('dd--ready');
        if (this.boundFallbackInput) {
          this.boundFallbackInput.setAttribute('name', 'attributes[Delivery Date]');
        }
      }

      // -- Setup -------------------------------------------------------------

      parseRules() {
        this.cutoffTime = this.dataset.cutoffTime || '14:00';
        const wd = parseInt(this.dataset.windowDays, 10);
        this.windowDays = Number.isFinite(wd) && wd > 0 ? wd : 90;

        const workingRaw = (this.dataset.workingDays || '1,2,3,4,5,6').split(',');
        // Normalise: treat 7 as 0 (Sunday) so we can compare against JS getDay().
        this.workingDays = new Set(
          workingRaw
            .map((d) => parseInt(d.trim(), 10))
            .filter((d) => Number.isFinite(d))
            .map((d) => (d === 7 ? 0 : d))
        );
        // Defensive: if parsing yielded nothing usable, fall back to Mon-Sat.
        if (this.workingDays.size === 0) {
          this.workingDays = new Set([1, 2, 3, 4, 5, 6]);
        }

        this.blackouts = new Set(
          (this.dataset.blackoutDates || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        );
      }

      boot() {
        const today = this.londonToday();
        this.today = today; // {y, m, d}

        // Window bounds (inclusive): today .. today + windowDays.
        this.minBound = today;
        this.maxBound = this.addDays(today, this.windowDays);

        // Earliest deliverable date given the cutoff.
        this.earliest = this.computeEarliest(today);

        // Prefer the date already on the cart (survives drawer re-renders), but
        // only if it is still valid; otherwise auto-select the earliest date.
        const current = (this.dataset.currentDate || '').trim();
        if (current && this.isSelectable(this.parseISO(current))) {
          this.selected = current;
          this.showSummary();
          this.setCheckoutEnabled(true);
        } else {
          // Auto-select the earliest available date and write it immediately so
          // a date is always set (belt-and-braces for the required-field rule).
          this.selected = this.toISO(this.earliest);
          this.writeAttribute(this.selected);
          this.showSummary();
          this.setCheckoutEnabled(true);
        }

        // Initial view month is the month of the selected (or earliest) date.
        const anchor = this.selected ? this.parseISO(this.selected) : this.earliest;
        this.viewMonth = { y: anchor.y, m: anchor.m };
      }

      // -- London "now" ------------------------------------------------------

      /*
       * Returns today's calendar date in Europe/London as {y, m, d} (m 0-11).
       * Uses Intl parts so it is correct regardless of the visitor's timezone
       * and across the BST/GMT switch.
       */
      londonToday() {
        const parts = this.londonParts();
        return { y: parts.year, m: parts.month - 1, d: parts.day };
      }

      londonParts() {
        const fmt = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        const map = {};
        for (const p of fmt.formatToParts(new Date())) {
          if (p.type !== 'literal') map[p.type] = p.value;
        }
        // hour can come back as "24" at midnight in some engines; normalise.
        let hour = parseInt(map.hour, 10);
        if (hour === 24) hour = 0;
        return {
          year: parseInt(map.year, 10),
          month: parseInt(map.month, 10),
          day: parseInt(map.day, 10),
          hour,
          minute: parseInt(map.minute, 10),
        };
      }

      /*
       * Earliest deliverable date: tomorrow if London-now is before cutoff,
       * else the day after tomorrow. Then roll forward past any non-working day
       * (e.g. Sunday) or blackout date. Never same-day.
       */
      computeEarliest(today) {
        const now = this.londonParts();
        const [ch, cm] = this.cutoffTime.split(':').map((n) => parseInt(n, 10));
        const cutoffMinutes = (Number.isFinite(ch) ? ch : 14) * 60 + (Number.isFinite(cm) ? cm : 0);
        const nowMinutes = now.hour * 60 + now.minute;

        // Before cutoff -> tomorrow; at/after cutoff -> day after tomorrow.
        let candidate = this.addDays(today, nowMinutes < cutoffMinutes ? 1 : 2);

        // Roll forward past non-working / blackout days. Bounded by the window.
        let guard = 0;
        while (!this.isDeliverable(candidate) && guard < 400) {
          candidate = this.addDays(candidate, 1);
          guard += 1;
        }
        return candidate;
      }

      // -- Availability ------------------------------------------------------

      // Working day + not a blackout. Does not consider the cutoff/window.
      isDeliverable(date) {
        const dow = this.dayOfWeek(date);
        if (!this.workingDays.has(dow)) return false;
        if (this.blackouts.has(this.toISO(date))) return false;
        return true;
      }

      // Fully selectable: deliverable AND within [earliest .. maxBound].
      isSelectable(date) {
        if (!date) return false;
        if (this.cmp(date, this.earliest) < 0) return false;
        if (this.cmp(date, this.maxBound) > 0) return false;
        return this.isDeliverable(date);
      }

      // -- Rendering ---------------------------------------------------------

      openCalendar() {
        if (this.summaryEl) this.summaryEl.hidden = true;
        if (this.calendarEl) this.calendarEl.hidden = false;
        this.renderCalendar();
        // Focus the selected (or first selectable) day for keyboard users.
        const focusTarget =
          this.calendarEl.querySelector('[aria-selected="true"]') ||
          this.calendarEl.querySelector('button.delivery-date__day:not([disabled])');
        if (focusTarget) focusTarget.focus();
      }

      showSummary() {
        if (this.selectedEl && this.selected) {
          this.selectedEl.textContent = this.friendly(this.parseISO(this.selected));
        }
        if (this.summaryEl) this.summaryEl.hidden = false;
        if (this.calendarEl) this.calendarEl.hidden = true;
        if (this.errorEl) this.errorEl.hidden = true;
      }

      renderCalendar() {
        const { y, m } = this.viewMonth;
        const monthName = new Intl.DateTimeFormat('en-GB', {
          month: 'long',
          year: 'numeric',
          timeZone: 'Europe/London',
        }).format(new Date(Date.UTC(y, m, 1)));

        const lastDayOfView = this.daysInMonth(y, m);
        // Can page back if any earlier month still contains the min bound or later.
        const canPrev = this.cmp({ y, m, d: 1 }, { y: this.minBound.y, m: this.minBound.m, d: 1 }) > 0;
        // Can page forward if the last day of this view is before the max bound.
        const canNext = this.cmp({ y, m, d: lastDayOfView }, this.maxBound) < 0;

        const weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

        let html = '';
        html += '<div class="delivery-date__nav">';
        html +=
          '<button type="button" class="delivery-date__nav-btn" data-dd-prev aria-label="Previous month"' +
          (canPrev ? '' : ' disabled') +
          '>&#8249;</button>';
        html += '<span class="delivery-date__month" aria-live="polite">' + monthName + '</span>';
        html +=
          '<button type="button" class="delivery-date__nav-btn" data-dd-next aria-label="Next month"' +
          (canNext ? '' : ' disabled') +
          '>&#8250;</button>';
        html += '</div>';

        html += '<div class="delivery-date__grid" role="grid" aria-label="Choose a delivery date">';
        html += '<div class="delivery-date__weekdays" role="row">';
        for (const wl of weekdayLabels) {
          html += '<span class="delivery-date__weekday" role="columnheader" aria-label="' + wl + '">' + wl + '</span>';
        }
        html += '</div>';
        html += '<div class="delivery-date__days">';

        // Leading blanks so day 1 sits under the right weekday (Mon-first grid).
        const firstDow = this.dayOfWeek({ y, m, d: 1 }); // 0=Sun..6=Sat
        const lead = (firstDow + 6) % 7; // convert to Mon-first offset
        for (let i = 0; i < lead; i += 1) {
          html += '<span class="delivery-date__day delivery-date__day--blank" aria-hidden="true"></span>';
        }

        for (let day = 1; day <= lastDayOfView; day += 1) {
          const date = { y, m, d: day };
          const iso = this.toISO(date);
          const selectable = this.isSelectable(date);
          const isSelected = this.selected === iso;
          const cls = ['delivery-date__day'];
          if (isSelected) cls.push('is-selected');
          let attrs = 'type="button" class="' + cls.join(' ') + '" data-date="' + iso + '" role="gridcell"';
          attrs += ' tabindex="' + (isSelected ? '0' : '-1') + '"';
          if (isSelected) attrs += ' aria-selected="true"';
          if (selectable) {
            attrs += ' aria-label="' + this.friendly(date) + '"';
          } else {
            attrs += ' disabled aria-disabled="true" aria-label="' + this.friendly(date) + ' (unavailable)"';
          }
          html += '<button ' + attrs + '>' + day + '</button>';
        }

        html += '</div></div>';
        this.calendarEl.innerHTML = html;

        const prevBtn = this.calendarEl.querySelector('[data-dd-prev]');
        const nextBtn = this.calendarEl.querySelector('[data-dd-next]');
        if (prevBtn) prevBtn.addEventListener('click', () => this.changeMonth(-1));
        if (nextBtn) nextBtn.addEventListener('click', () => this.changeMonth(1));

        this.calendarEl.querySelectorAll('button.delivery-date__day:not([disabled])').forEach((btn) => {
          btn.addEventListener('click', () => this.selectDate(btn.dataset.date));
        });
      }

      changeMonth(delta) {
        let { y, m } = this.viewMonth;
        m += delta;
        if (m < 0) {
          m = 11;
          y -= 1;
        } else if (m > 11) {
          m = 0;
          y += 1;
        }
        // Clamp within bounds.
        if (this.cmp({ y, m, d: this.daysInMonth(y, m) }, this.minBound) < 0) return;
        if (this.cmp({ y, m, d: 1 }, this.maxBound) > 0) return;
        this.viewMonth = { y, m };
        this.renderCalendar();
        const monthLabel = this.calendarEl.querySelector('.delivery-date__month');
        if (monthLabel) monthLabel.focus && monthLabel.focus();
      }

      onGridKeydown(e) {
        const focused = document.activeElement;
        if (!focused || !focused.classList.contains('delivery-date__day')) return;
        const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
        if (!(e.key in moves)) return;
        e.preventDefault();
        const current = this.parseISO(focused.dataset.date);
        let target = this.addDays(current, moves[e.key]);
        // Step in the same direction until we land on a selectable day in bounds.
        const step = moves[e.key] > 0 ? 1 : -1;
        let guard = 0;
        while (guard < 400) {
          if (this.cmp(target, this.earliest) < 0 || this.cmp(target, this.maxBound) > 0) return;
          if (this.isSelectable(target)) break;
          target = this.addDays(target, step);
          guard += 1;
        }
        // Re-render the month if we crossed a boundary, then focus the target.
        if (target.y !== this.viewMonth.y || target.m !== this.viewMonth.m) {
          this.viewMonth = { y: target.y, m: target.m };
          this.renderCalendar();
        }
        const btn = this.calendarEl.querySelector('[data-date="' + this.toISO(target) + '"]');
        if (btn) {
          this.calendarEl
            .querySelectorAll('[tabindex="0"]')
            .forEach((el) => el.setAttribute('tabindex', '-1'));
          btn.setAttribute('tabindex', '0');
          btn.focus();
        }
      }

      // -- Selection + cart write -------------------------------------------

      selectDate(iso) {
        const date = this.parseISO(iso);
        if (!this.isSelectable(date)) return;
        this.selected = iso;
        this.writeAttribute(iso);
        this.showSummary();
        this.setCheckoutEnabled(true);
      }

      writeAttribute(iso) {
        if (this.input) this.input.value = iso;
        // Keep the native fallback's value in sync so the two controls never
        // disagree if CSS fails to hide the fallback.
        if (this.boundFallbackInput) this.boundFallbackInput.value = iso;
        const body = JSON.stringify({ attributes: { 'Delivery Date': iso } });
        // Reuse the theme's shared fetch helper if present, else a sane default.
        const cfg = typeof fetchConfig === 'function' ? fetchConfig() : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
        const url = (typeof routes !== 'undefined' && routes.cart_update_url) || '/cart/update.js';
        fetch(url, { ...cfg, body }).catch(() => {
          /* Non-fatal: the hidden input still submits with the form. */
        });
      }

      setCheckoutEnabled(enabled) {
        const btn = document.querySelector('[data-dd-checkout]');
        if (!btn) return;
        // Never re-enable an empty cart's button.
        const cartEmpty = document.querySelector('cart-drawer.is-empty');
        if (enabled && !cartEmpty) {
          btn.removeAttribute('disabled');
          if (this.errorEl) this.errorEl.hidden = true;
        } else if (!enabled) {
          btn.setAttribute('disabled', 'disabled');
          if (this.errorEl) this.errorEl.hidden = false;
        }
      }

      // -- Date helpers (all on calendar Y/M/D, no UTC drift) ----------------

      addDays(date, n) {
        const dt = new Date(Date.UTC(date.y, date.m, date.d + n));
        return { y: dt.getUTCFullYear(), m: dt.getUTCMonth(), d: dt.getUTCDate() };
      }

      dayOfWeek(date) {
        // 0=Sun .. 6=Sat, computed in UTC to avoid local-timezone shifts.
        return new Date(Date.UTC(date.y, date.m, date.d)).getUTCDay();
      }

      daysInMonth(y, m) {
        return new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
      }

      cmp(a, b) {
        if (a.y !== b.y) return a.y - b.y;
        if (a.m !== b.m) return a.m - b.m;
        return a.d - b.d;
      }

      toISO(date) {
        const mm = String(date.m + 1).padStart(2, '0');
        const dd = String(date.d).padStart(2, '0');
        return date.y + '-' + mm + '-' + dd;
      }

      parseISO(iso) {
        const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
        return { y, m: m - 1, d };
      }

      friendly(date) {
        // e.g. "Friday 13 June"
        return new Intl.DateTimeFormat('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          timeZone: 'Europe/London',
        }).format(new Date(Date.UTC(date.y, date.m, date.d)));
      }
    }
  );
}
